/**
 * Whether the Vendor List shows its "Done with Vendor List" onboarding footer. It is a forward
 * path through the data map onboarding, so it is gone once the org has finished onboarding
 * entirely (`finishedOnboardingLatch`), whatever state its vendors are in. Until then it follows
 * the latched first-run/incomplete-vendor signal or the live onboarding progress, and steps out of
 * the way while a vendor's settings are open.
 */
export function shouldShowVendorListOnboardingFooter({
  finishedOnboardingLatch,
  showFooter,
  isDataMapOnboardingIncomplete,
  isSidebarOpen,
}: {
  finishedOnboardingLatch: boolean;
  showFooter: boolean;
  isDataMapOnboardingIncomplete: boolean;
  isSidebarOpen: boolean;
}): boolean {
  if (finishedOnboardingLatch) {
    return false;
  }
  return showFooter || (isDataMapOnboardingIncomplete && !isSidebarOpen);
}

export type VendorListCompletion = {
  /** The vendor whose settings finished the list, when that is clear. */
  vendorName?: string;
};

/**
 * Whether this update finished the Vendor List: a vendor was incomplete before it and none is
 * now. The finishing vendor is named only when exactly one was incomplete and it is still listed,
 * so removing the last incomplete vendor completes the list without naming anyone.
 */
export function vendorListJustCompleted<V extends { vendorId: string; name: string }>({
  previousIncomplete,
  incomplete,
  vendors,
}: {
  previousIncomplete: V[] | null;
  incomplete: V[];
  vendors: V[];
}): VendorListCompletion | null {
  if (!previousIncomplete || previousIncomplete.length === 0 || incomplete.length > 0) {
    return null;
  }
  const finishingVendor =
    previousIncomplete.length === 1
      ? vendors.find((v) => v.vendorId === previousIncomplete[0].vendorId)
      : undefined;
  return { vendorName: finishingVendor?.name };
}
